import { faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Flex, Stack, useMantineTheme } from '@mantine/core';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { MAX_SITELINK_LINK_TEXT, MAX_SITELINKS_PER_AD_GROUP } from '@feathr/blackbox';
import { Alert, Button, Input, Label, Well } from '@feathr/components';

import { createSitelink } from '../AdStep.factories';
import type { ISitelink, ISitelinkRowErrors } from '../AdStep.types';
import { AD_GRANT_LIMITS } from '../AdStep.utils';

import * as styles from './AdGroupEditor.css';

interface ISitelinkFieldsProps {
  sitelinks: ISitelink[];
  /** Messages for each row that has any, keyed by sitelink key. */
  sitelinkErrors: Record<string, ISitelinkRowErrors>;
  onChange: (sitelinks: ISitelink[]) => void;
  disabled?: boolean;
}

/**
 * The ad group's sitelinks, which every ad in the group shares.
 *
 * @param props - {@link ISitelinkFieldsProps}.
 */
function SitelinkFields({
  sitelinks,
  sitelinkErrors,
  onChange,
  disabled = false,
}: Readonly<ISitelinkFieldsProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const isBelowMinimum = sitelinks.length < AD_GRANT_LIMITS.minSitelinksPerAdGroup;
  const isAtMaximum = sitelinks.length >= MAX_SITELINKS_PER_AD_GROUP;

  function handleAdd(): void {
    onChange([...sitelinks, createSitelink()]);
  }

  function handleRemove(index: number): () => void {
    return function (): void {
      onChange(sitelinks.filter((_, i) => i !== index));
    };
  }

  function handleChange(index: number, field: 'text' | 'url'): (value?: string) => void {
    return function (value?: string): void {
      onChange(
        sitelinks.map((sitelink, i) =>
          i === index ? { ...sitelink, [field]: value ?? '' } : sitelink,
        ),
      );
    };
  }

  return (
    <>
      <Label weight={'medium'}>{t('Sitelinks')}</Label>
      <Well layout={'vertical'} theme={'white'} width={'full'}>
        <Alert
          className={styles.alertInWell}
          title={t(
            'Ad Grant policy requires at least {{minimum}} sitelinks per ad group. This ad group has {{actual}}.',
            { actual: sitelinks.length, minimum: AD_GRANT_LIMITS.minSitelinksPerAdGroup },
          )}
          type={isBelowMinimum ? 'danger' : 'info'}
        >
          {t('Sitelinks are shared by every ad in this ad group.')}
        </Alert>
        {/* A Stack, so rows sit 16px apart rather than at the Well's 8px gap. */}
        <Stack gap={theme.spacing['4']}>
          {sitelinks.map((sitelink, index) => {
            const errors = sitelinkErrors[sitelink.key];
            return (
              // Top-aligned: only the link text carries a counter, and it hangs below that
              // field, so centring would push the URL field and delete button down.
              <Flex align={'flex-start'} className={styles.addRow} key={sitelink.key} wrap={'wrap'}>
                {/* No visible labels — the placeholders carry the meaning, and a repeated
                    "Link text N" reads as noise. A placeholder is not an accessible name. */}
                <Input
                  aria-label={t('Link text {{number}}', { number: index + 1 })}
                  disabled={disabled}
                  maxLength={MAX_SITELINK_LINK_TEXT}
                  onChange={handleChange(index, 'text')}
                  placeholder={t('Link text, e.g. Donate Now')}
                  showCounter={true}
                  type={'text'}
                  validationError={errors?.text.length ? errors.text : undefined}
                  value={sitelink.text}
                  wrapperClassName={styles.grow}
                />
                <Input
                  aria-label={t('URL {{number}}', { number: index + 1 })}
                  disabled={disabled}
                  onChange={handleChange(index, 'url')}
                  placeholder={t('URL, e.g. https://example.org/donate')}
                  type={'text'}
                  validationError={errors?.url.length ? errors.url : undefined}
                  value={sitelink.url}
                  wrapperClassName={styles.growWide}
                />
                <Button
                  aria-label={t('Remove sitelink {{number}}', { number: index + 1 })}
                  disabled={disabled}
                  name={`remove-sitelink-${index + 1}`}
                  onClick={handleRemove(index)}
                  tooltip={t('Remove sitelink')}
                  type={'icon'}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </Button>
              </Flex>
            );
          })}
        </Stack>
        <Button
          disabled={disabled || isAtMaximum}
          name={'add-sitelink'}
          onClick={handleAdd}
          prefix={<FontAwesomeIcon icon={faPlus} />}
          tooltip={
            isAtMaximum
              ? t('An ad group can have at most {{maximum}} sitelinks.', {
                  maximum: MAX_SITELINKS_PER_AD_GROUP,
                })
              : undefined
          }
        >
          {t('Add sitelink')}
        </Button>
      </Well>
    </>
  );
}

export default SitelinkFields;
